import React, { useRef, useState } from "react";
import { useHistory } from "react-router";
import { CreateRotationSchemeServices } from "../../../../Services";
import { CustomButton, CustomDialog, PageTitle } from "../../../../Components";
import {
  AddRotationStepper,
  AddNewRotationForm,
  SchemaSettingsForm,
} from "../";
import { useVerticalNav } from "../../../../Contexts/VerticalNavContext";
import { buildQueryBody } from "../util";
import { useTranslate } from "../../../../Hooks";

import useStyles from "../Styles";

import { CheckCircleOutlined } from "../../../../assets/icons";

function NewAddAgentsRotationCriteriaView() {
  const [activeStep, setActiveStep] = useState(0);
  const [formValues, setFormValues] = useState({
    addRotationForm: {},
    schemaSettingsForm: {},
  });
  const [activeFields, setActiveFields] = useState(null);
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const { setAlertBoxContent } = useVerticalNav();
  const styles = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const addRotationFormikRef = useRef();
  const schemaSettingsFormikRef = useRef();
  const navigate = useHistory();

  const saveSchema = () => {
    const body = buildQueryBody(formValues);
    const res = CreateRotationSchemeServices(body);
    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate(`create-schema-modal-title`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      setActiveStep(activeStep + 1);
    } else {
      const errorMessage = (res && res.data && res.data.Message).split(": ")[1];
      if (errorMessage === "___DUPLICATE_ROTATION_SCHEME_ORDER___")
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: "Failed to Create Schema.",
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      else
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("Shared:Server-Error"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
    }
  };

  const saveActiveFieldSelection = (fields) => {
    setActiveFields({ ...fields });
  };

  const stepperSchema = {
    steps: [
      {
        stepLabel: translate("rotation-criteria"),
        actionButtons: [
          {
            labelText: translate("next"),
            onClickHandler: (data) => {
              addRotationFormikRef.current.validateForm().then((errors) => {
                const isValid = Object.keys(errors).length === 0;
                if (isValid) {
                  setFormValues({
                    ...formValues,
                    addRotationForm: addRotationFormikRef.current.values,
                  });
                  saveActiveFieldSelection(data);
                  setActiveStep(activeStep + 1);
                }
                const touchedFields = Object.keys(errors).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {});

                addRotationFormikRef.current.setTouched(touchedFields, true);
                addRotationFormikRef.current.setErrors(errors);
              });
            },
            type: "primary",
            isShown: true,
          },
          {
            labelText: translate("cancel"),
            onClickHandler: () => {
              navigate.replace("/home/rotation-criteria/view");
            },
            type: "secondary",
            isShown: true,
          },
        ],
      },
      {
        stepLabel: translate("schema-setting"),
        actionButtons: [
          {
            labelText: translate("create-continue"),
            onClickHandler: () => {
              setFormValues({
                ...formValues,
                schemaSettingsForm: {
                  ...schemaSettingsFormikRef.current.values,
                },
              });
              setIsModalOpen(true);
            },
            type: "primary",
            isShown: true,
          },

          {
            labelText: translate("back"),
            onClickHandler: () => {
              setFormValues({
                ...formValues,
                schemaSettingsForm: {
                  ...schemaSettingsFormikRef.current.values,
                },
              });
              if (activeStep === 0) return;
              setActiveStep(activeStep - 1);
            },
            type: "secondary",
            isShown: true,
          },
          {
            labelText: translate("cancel"),
            onClickHandler: () => {
              navigate.replace("/home/rotation-criteria/view");
            },
            type: "secondary",
            isShown: true,
          },
        ],
      },
      {
        stepLabel: translate("asign-agent"),
        actionButtons: [
          {
            labelText: translate("next"),
            onClickHandler: () => {
              if (activeStep + 1 >= stepperSchema.steps.length) return;
              setActiveStep(activeStep + 1);
            },
            type: "primary",
            isShown: true,
          },

          {
            labelText: translate("back"),
            onClickHandler: () => {
              if (activeStep === 0) return;
              setActiveStep(activeStep - 1);
            },
            type: "secondary",
            isShown: true,
          },
          {
            labelText: translate("cancel"),
            onClickHandler: () => {},
            type: "secondary",
            isShown: true,
          },
        ],
      },
    ],
  };

  const generateStepperActions = (data) => {
    // generate the action buttons related to current active step
    return stepperSchema.steps[activeStep].actionButtons.map(
      ({ isShown, type, labelText, onClickHandler }, index) => {
        return isShown ? (
          <CustomButton
            key={`${stepperSchema.steps[activeStep].stepLabel}_${labelText}`}
            boxShadow="xs"
            size="md"
            variant={type === "secondary" ? "outlined" : ""}
            color={type}
            id={`${type}_${labelText}`}
            onClick={() => onClickHandler(data)}
          >
            {labelText}
          </CustomButton>
        ) : null;
      }
    );
  };

  return (
    <>
      <PageTitle
        title={translate("add-agent-rotation-page-title")}
        subTitle={translate("add-agent-rotation-page-subtitle")}
        wrapperClasses={styles.pageTitle}
        hideDivider
      />
      <AddRotationStepper
        activeStep={activeStep}
        stepperSchema={stepperSchema}
      />
      {activeStep === 0 && (
        <AddNewRotationForm
          generateStepperActions={generateStepperActions}
          formikRef={addRotationFormikRef}
          formValues={formValues.addRotationForm}
          activeOptionalFields={activeFields}
        />
      )}
      {activeStep === 1 && (
        <SchemaSettingsForm
          formikRef={schemaSettingsFormikRef}
          generateStepperActions={generateStepperActions}
          formValues={formValues.schemaSettingsForm}
        />
      )}
      <CustomDialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        hideActionsBorder
        onConfirm={() => {
          saveSchema();
          setIsModalOpen(false);
        }}
        title={translate("create-schema-modal-title")}
        subtitle={translate("create-schema-modal-sub-text")}
        cancelText={translate("cancel")}
        confirmText={translate("confirm")}
        width="544px"
        headingIcon={
          <div className={styles.modalIcon}>
            <CheckCircleOutlined width="20" height="20" fill={"#079455"} />
          </div>
        }
      />
    </>
  );
}

export default NewAddAgentsRotationCriteriaView;
